/**
 * PostHog Initialization
 * Initializes PostHog analytics for the application
 *
 * Initialization is deferred until after React hydration to avoid
 * "Maximum call stack size exceeded" when capture/autocapture runs
 * during SPA hydration (see PostHog/posthog-js#1303).
 */

// IMPORTANT:
// Do NOT import `posthog-js` at module scope.
// In Jest/SSR environments `location` may be undefined and `posthog-js` will crash on import.
let _posthog = null;

const getPosthogClient = () => {
  // Check if we're in a real browser environment
  if (
    typeof window === "undefined" ||
    typeof window.location === "undefined" ||
    typeof document === "undefined"
  ) {
    return null;
  }

  if (_posthog) return _posthog;

  try {
    // eslint-disable-next-line global-require
    const mod = require("posthog-js");
    _posthog = mod?.default ?? mod;
    return _posthog;
  } catch (e) {
    return null;
  }
};

const POSTHOG_DISABLED_KEY = "posthog_disabled";

/**
 * Check if PostHog was disabled for this session (e.g. after a stack overflow)
 */
export const isPostHogDisabled = () => {
  if (typeof window === "undefined") return true;
  try {
    return window.sessionStorage.getItem(POSTHOG_DISABLED_KEY) === "1";
  } catch {
    return false;
  }
};

/**
 * Disable PostHog for this session so one crash does not break the page
 */
export const disablePostHogForSession = () => {
  try {
    window.sessionStorage.setItem(POSTHOG_DISABLED_KEY, "1");
  } catch (_) {}

  // posthog-js drives `capture_pageview: "history_change"` from its own
  // internal instance reference, not from window.posthog, so nulling the
  // global below does not stop it. Turn the listener off on the live instance.
  // set_config (not opt_out_capturing) — the latter persists to
  // localStorage/cookie and would opt the visitor out across future sessions.
  try {
    window.posthog?.set_config?.({ capture_pageview: false });
  } catch (_) {}

  try {
    window.posthog = null;
  } catch (_) {}
};

/**
 * Initialize PostHog
 * Should be called once when the app loads. Deferred to avoid stack overflow during hydration.
 */
export const initPostHog = () => {
  if (typeof window === "undefined") return;
  if (isPostHogDisabled()) return;

  const posthogApiKey = process.env.GATSBY_POSTHOG_API_KEY;
  const posthogHost =
    process.env.GATSBY_POSTHOG_HOST || "https://us.i.posthog.com";

  if (!posthogApiKey) return;

  // If parent/iframe already has a different PostHog, skip our init to avoid reset+init loops
  if (window.posthog && typeof window.posthog.capture === "function") {
    const existingToken = window.posthog?.config?.token;
    if (existingToken === posthogApiKey) {
      return;
    }
    // Different project: do not reset global posthog (can cause stack overflow).
    // Skip initializing our token in this context so the page still works.
    return;
  }

  const doInit = () => {
    if (isPostHogDisabled()) return;

    // Resolve the client lazily. `posthog-js` is intentionally not imported at
    // module scope (see note above), so it MUST be pulled in through
    // getPosthogClient() here – referencing a bare `posthog` throws
    // ReferenceError and silently kills all tracking.
    const posthog = getPosthogClient();
    if (!posthog) return;

    try {
      posthog.init(posthogApiKey, {
        api_host: posthogHost,
        loaded: (posthogInstance) => {
          window.posthog = posthogInstance;
        },
        // Let posthog-js own pageviews: it sends the initial one just after
        // `loaded` and then one per pushState/replaceState/popstate, deduped
        // on pathname. Init is already deferred past hydration below, so this
        // no longer risks the "Maximum call stack size exceeded" crash.
        capture_pageview: "history_change",
        capture_pageleave: true,
      });
    } catch (error) {
      console.warn("[PostHog] Initialization failed:", error);
      disablePostHogForSession();
    }
  };

  // Defer init until after React hydration to prevent stack overflow (posthog-js#1303)
  if (typeof requestIdleCallback !== "undefined") {
    requestIdleCallback(doInit, { timeout: 2000 });
  } else {
    setTimeout(doInit, 0);
  }
};

/**
 * Check if PostHog is initialized and not disabled
 */
export const isPostHogInitialized = () => {
  return (
    typeof window !== "undefined" &&
    !isPostHogDisabled() &&
    window.posthog &&
    typeof window.posthog.capture === "function"
  );
};
